// We need to use direct localStorage access rather than importing from storage.js
// to avoid circular dependencies
import { GLOBAL_CHANTER_KEY, GLOBAL_MIC_KEY } from './constants';

// Schema versions
export const SCHEMA_VERSION = 2.0;
export const OLD_SCHEMA_VERSION = 1.0;

// Keys for global settings
export const SCHEMA_VERSION_KEY = 'bagpipe_app_schema_version';

// Safe localStorage getItem with JSON parsing
function safeGetItem(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return defaultValue;
  }
}

// Safe localStorage setItem with JSON stringifying
function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
    return false;
  }
}

/**
 * Check if migration is needed
 * @returns {boolean} True if migration is needed
 */
export function needsMigration() {
  try {
    const currentVersion = localStorage.getItem(SCHEMA_VERSION_KEY);
    const version = currentVersion ? parseFloat(currentVersion) : OLD_SCHEMA_VERSION;
    return version !== SCHEMA_VERSION;
  } catch (error) {
    console.error('Error checking migration status:', error);
    return false;
  }
}

/**
 * Get current schema version
 * @returns {number} Current schema version
 */
export function getCurrentVersion() {
  const version = localStorage.getItem(SCHEMA_VERSION_KEY);
  return version ? parseFloat(version) : OLD_SCHEMA_VERSION;
}

/**
 * Migration from v1.0 to v2.0
 * Consolidates microphone and chanter selections into global settings
 * @returns {Object} Migration result with selected microphone and chanter
 */
function migrateFrom1To2() {
  console.log('Migrating from v1.0 to v2.0: Consolidating audio device and chanter selections');
  
  // Get all component-specific settings
  const battlePrefs = safeGetItem('battle_preferences', {});
  const blasterPrefs = safeGetItem('blaster_preferences', {});
  const chanterTestPrefs = safeGetItem('chantertest_preferences', {});
  const playAlongPrefs = safeGetItem('playalong_preferences', {});
  const flashcardPrefs = safeGetItem('flashcard_preferences', {});
  
  // Prioritize microphone selection
  const micSources = [
    { source: 'Battle', device: battlePrefs.selectedDevice },
    { source: 'Blaster', device: blasterPrefs.selectedDevice },
    { source: 'ChanterTest', device: chanterTestPrefs.selectedDevice },
    { source: 'PlayAlong', device: playAlongPrefs.selectedDevice }
  ];
  
  // Find first non-null microphone
  const micSource = micSources.find(s => s.device);
  const selectedMic = micSource?.device || null;
  
  // Prioritize chanter selection
  const chanterSources = [
    { source: 'Battle', chanter: battlePrefs.selectedChanter },
    { source: 'Blaster', chanter: blasterPrefs.selectedChanter },
    { source: 'ChanterTest', chanter: chanterTestPrefs.selectedChanter },
    { source: 'PlayAlong', chanter: playAlongPrefs.selectedChanter },
    { source: 'FlashCard', chanter: flashcardPrefs.selectedChanter }
  ];
  
  // Find first non-null chanter
  const chanterSource = chanterSources.find(s => s.chanter);
  const selectedChanter = chanterSource?.chanter || 'Standard';
  
  // Save as global settings
  safeSetItem(GLOBAL_MIC_KEY, selectedMic);
  safeSetItem(GLOBAL_CHANTER_KEY, selectedChanter);
  
  // Store migration metadata for debugging
  safeSetItem('migration_v1_to_v2_info', {
    date: new Date().toISOString(),
    selectedMicSource: micSource?.source || 'None',
    selectedChanterSource: chanterSource?.source || 'Default'
  });
  
  return {
    microphone: selectedMic,
    micSource: micSource?.source || 'None',
    chanter: selectedChanter,
    chanterSource: chanterSource?.source || 'Default'
  };
}

/**
 * Define all migrations in sequence
 * Each migration transforms from one version to the next
 */
const migrations = [
  { from: 1.0, to: 2.0, migrate: migrateFrom1To2 }
  // Future migrations will be added here
  // { from: 2.0, to: 3.0, migrate: migrateFrom2To3 }
];

/**
 * Run all necessary migrations in sequence
 * @returns {Object} Migration results or false if no migrations were run
 */
export function runMigrations() {
  const currentVersion = getCurrentVersion();
  const targetVersion = SCHEMA_VERSION;
  
  if (currentVersion === targetVersion) {
    console.log(`No migrations needed. Current version: ${currentVersion}`);
    return false;
  }
  
  console.log(`Starting migrations from v${currentVersion} to v${targetVersion}`);
  
  let version = currentVersion;
  const results = {};
  
  // Find all migrations that need to be run
  const neededMigrations = migrations
    .filter(m => m.from >= currentVersion && m.to <= targetVersion)
    .sort((a, b) => a.from - b.from);
  
  // Run migrations in sequence
  for (const migration of neededMigrations) {
    if (version === migration.from) {
      console.log(`Running migration from v${migration.from} to v${migration.to}`);
      
      try {
        const result = migration.migrate();
        results[`v${migration.from}_to_v${migration.to}`] = result;
        version = migration.to;
        
        // Update version after each successful migration
        localStorage.setItem(SCHEMA_VERSION_KEY, version.toString());
        console.log(`Migration to v${version} completed successfully`);
      } catch (error) {
        console.error(`Migration from v${migration.from} to v${migration.to} failed:`, error);
        // Don't continue with further migrations if one fails
        return {
          error: `Migration from v${migration.from} to v${migration.to} failed: ${error.message}`,
          completedMigrations: results
        };
      }
    }
  }
  
  // Log completion
  if (Object.keys(results).length > 0) {
    console.log(`All migrations completed successfully. Current version: ${version}`);
    return results;
  } else {
    console.log(`No applicable migrations found for version ${currentVersion}`);
    return false;
  }
}

/**
 * Complete setup by updating schema version
 * Use this when you want to skip migrations and just update the version
 */
export function completeSetup() {
  console.log(`Updating schema version to ${SCHEMA_VERSION} without migrations`);
  localStorage.setItem(SCHEMA_VERSION_KEY, SCHEMA_VERSION.toString());
  return true;
}

/**
 * Get global settings 
 * @returns {Object} Global settings
 */
export function getGlobalSettings() {
  return {
    microphone: safeGetItem(GLOBAL_MIC_KEY, null),
    chanter: safeGetItem(GLOBAL_CHANTER_KEY, 'Standard'),
    schemaVersion: getCurrentVersion()
  };
}

/**
 * Initialize global settings if needed
 * Can be called early in the app lifecycle
 */
export function initializeGlobalSettings() {
  // Check for existing global settings
  const mic = safeGetItem(GLOBAL_MIC_KEY, null);
  const chanter = safeGetItem(GLOBAL_CHANTER_KEY, 'Standard');
  
  // Get current schema version
  const version = getCurrentVersion();
  
  console.log(`Initializing global settings. Current version: ${version}`);
  console.log(`Global microphone: ${mic || 'Not set'}`);
  console.log(`Global chanter: ${chanter || 'Not set'}`);
  
  return {
    version,
    mic,
    chanter,
    needsMigration: needsMigration()
  };
}

// Additional keys for settings management
const MIGRATION_NOTICE_DISMISSED_KEY = 'migration_notice_dismissed';

/**
 * Check if the migration notice has been dismissed
 * @returns {boolean} true if the notice has been dismissed
 */
export function isMigrationNoticeDismissed() {
  return safeGetItem(MIGRATION_NOTICE_DISMISSED_KEY, false);
}

/**
 * Dismiss the migration notice
 * @returns {boolean} true if successful
 */
export function dismissMigrationNotice() {
  return safeSetItem(MIGRATION_NOTICE_DISMISSED_KEY, true);
}

/**
 * Check if the application should show a migration notice
 * @returns {boolean} true if a notice should be shown
 */
export function shouldShowMigrationNotice() {
  return needsMigration() && !isMigrationNoticeDismissed();
} 